import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { CATEGORIES, COMMISSION_RATE, DEMO_LISTINGS, calcFees, formatCurrencyByCountry } from '../lib/constants'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import AvailabilityCalendar from '../components/AvailabilityCalendar'
import FormattedText from '../components/FormattedText'
import { MAX_BOOKING_DAYS, calculatePricing, formatHirePeriod, getBookingLimits } from '../lib/hirePricing'
import ListingPlaceholder from '../components/ListingPlaceholder'

const DEMO_MODE = process.env.NODE_ENV === 'development'
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

const daysBetween = (start, end) => {
if (!start || !end) return 1
const s = new Date(start)
const e = new Date(end)
const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1
return Math.max(1, diff)
}

// '2026-10-17' + 6 → '2026-10-23'
const addDays = (dateStr, n) => {
const d = new Date(dateStr)
d.setUTCDate(d.getUTCDate() + n)
return d.toISOString().split('T')[0]
}

export default function ListingDetail() {
const { id } = useParams()
const navigate = useNavigate()
const { user, profile } = useAuth()
const [listing, setListing] = useState(null)
const [loading, setLoading] = useState(true)
const [step, setStep] = useState('detail')

const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])
const [sameDayHours, setSameDayHours] = useState(4)
const [bookingMode, setBookingMode] = useState('multi')

const [cancellationCheck, setCancellationCheck] = useState(false)
const [insuranceStatus, setInsuranceStatus] = useState('')
const [termsCheck, setTermsCheck] = useState(false)
const [notes, setNotes] = useState('')
const [submitting, setSubmitting] = useState(false)
const [error, setError] = useState('')
const [currentImg, setCurrentImg] = useState(0)
const [copied, setCopied] = useState(false)
const [isFavourited, setIsFavourited] = useState(false)
const [enquiryMessage, setEnquiryMessage] = useState('')
const [sendingEnquiry, setSendingEnquiry] = useState(false)
const [showEnquiry, setShowEnquiry] = useState(false)
const [unavailableSet, setUnavailableSet] = useState(new Set())

// Check if viewer is the owner
const isOwner = user && listing && listing.owner_id === user.id

useEffect(() => {
if (DEMO_MODE) {
const demo = DEMO_LISTINGS.find(l => l.id === id)
setListing(demo || null)
setLoading(false)
} else {
fetchListing()
}
}, [id])

useEffect(() => {
if (!user || !listing) return
checkFavourite()
}, [user, listing])

useEffect(() => {
if (!listing) return
loadUnavailableDates()
}, [listing])

useEffect(() => {
if (!listing) return
const { allowSameDay, minHours, minDays } = getBookingLimits(listing)
if (bookingMode === 'sameday' && !allowSameDay) {
setBookingMode('multi')
return
}
if (bookingMode === 'sameday') {
setSameDayHours(h => (h && h < minHours ? minHours : h))
return
}
// Multi-day: push the end date out so the booking meets the minimum hire
const earliestEnd = addDays(startDate, minDays - 1)
setEndDate(prev => (prev < earliestEnd ? earliestEnd : prev))
}, [listing, bookingMode, startDate])

useEffect(() => {
if (!listing || !user) return
try {
const raw = localStorage.getItem('hireit_pending_booking')
if (!raw) return
const saved = JSON.parse(raw)
if (saved.listingId !== listing.id) return
if (Date.now() - (saved.savedAt || 0) > 2 * 60 * 60 * 1000) {
localStorage.removeItem('hireit_pending_booking')
return
}
if (saved.bookingMode && !(saved.bookingMode === 'sameday' && !getBookingLimits(listing).allowSameDay)) setBookingMode(saved.bookingMode)
if (saved.startDate) setStartDate(saved.startDate)
if (saved.endDate) setEndDate(saved.endDate)
if (typeof saved.sameDayHours === 'number') setSameDayHours(saved.sameDayHours)
if (saved.notes) setNotes(saved.notes)
localStorage.removeItem('hireit_pending_booking')
} catch {
localStorage.removeItem('hireit_pending_booking')
}
}, [listing, user])

useEffect(() => {
if (!listing || !user) return
try {
const raw = localStorage.getItem('hireit_pending_enquiry')
if (!raw) return
const saved = JSON.parse(raw)
if (saved.listingId !== listing.id) return
if (Date.now() - (saved.savedAt || 0) > 2 * 60 * 60 * 1000) {
localStorage.removeItem('hireit_pending_enquiry')
return
}
if (saved.draft) setEnquiryMessage(saved.draft)
setShowEnquiry(true)
localStorage.removeItem('hireit_pending_enquiry')
} catch {
localStorage.removeItem('hireit_pending_enquiry')
}
}, [listing, user])

const fetchListing = async () => {
const { data } = await supabase
.from('listings').select('*').eq('id', id).single()
if (data && data.owner_id) {
const { data: ownerRows } = await supabase
.from('public_profiles')
.select('id, full_name, rating, review_count, founding_lister_number, founding_lister_suburb')
.in('id', [data.owner_id])
const owner = (ownerRows && ownerRows[0]) || null
setListing({ ...data, profiles: owner })
} else {
setListing(data)
}
setLoading(false)
}

const loadUnavailableDates = async () => {
const set = new Set()
const { data: blocked } = await supabase
.from('listing_availability')
.select('blocked_date')
.eq('listing_id', listing.id)
;(blocked || []).forEach(b => set.add(b.blocked_date))

const { data: bookings } = await supabase
.from('bookings')
.select('start_date, hours')
.eq('listing_id', listing.id)
.in('status', ['confirmed', 'pending', 'paid', 'active', 'collected'])

;(bookings || []).forEach(b => {
if (!b.start_date) return
const start = new Date(b.start_date)
const days = Math.max(1, Math.ceil((b.hours || 0) / 24))
for (let i = 0; i < days; i++) {
const d = new Date(start)
d.setDate(d.getDate() + i)
const y = d.getFullYear()
const m = String(d.getMonth() + 1).padStart(2, '0')
const day = String(d.getDate()).padStart(2, '0')
set.add(`${y}-${m}-${day}`)
}
})
setUnavailableSet(set)
}

const checkFavourite = async () => {
const { data } = await supabase
.from('favourites').select('id')
.eq('user_id', user.id).eq('listing_id', listing.id).maybeSingle()
setIsFavourited(!!data)
}

const toggleFavourite = async () => {
if (!user) { navigate(`/auth?reason=favourite&next=/listing/${listing.id}`); return }
if (isFavourited) {
await supabase.from('favourites').delete().eq('user_id', user.id).eq('listing_id', listing.id)
setIsFavourited(false)
} else {
await supabase.from('favourites').insert({ user_id: user.id, listing_id: listing.id })
setIsFavourited(true)
}
}

const handleEnquiry = async () => {
if (!enquiryMessage.trim()) return
if (!user) {
localStorage.setItem('hireit_pending_enquiry', JSON.stringify({
listingId: listing.id,
draft: enquiryMessage.trim(),
savedAt: Date.now(),
}))
navigate(`/auth?next=/listing/${listing.id}&reason=message`)
return
}
setSendingEnquiry(true)
const { data: bookingData, error: bookErr } = await supabase.from('bookings').insert({
listing_id: listing.id,
hirer_id: user.id,
owner_id: listing.owner_id,
start_date: new Date().toISOString().split('T')[0],
hours: 1,
total_amount: 0,
platform_fee: 0,
owner_payout: 0,
deposit_amount: 0,
status: 'enquiry',
notes: enquiryMessage.trim(),
}).select().single()

if (!bookErr && bookingData) {
await supabase.from('messages').insert({
booking_id: bookingData.id,
sender_id: user.id,
body: enquiryMessage.trim(),
read: false,
})
navigate(`/messages?booking=${bookingData.id}`)
}
setSendingEnquiry(false)
}

const cat = listing ? CATEGORIES.find(c => c.id === listing.category_id) : null
const country = listing?.country || 'Australia'

const pricing = listing
? calculatePricing({ listing, bookingMode, days: daysBetween(startDate, endDate), sameDayHours: sameDayHours || 0, fmt: v => formatCurrencyByCountry(v, country) })
: { subtotal: 0, breakdown: '' }
const totalHours = bookingMode === 'sameday' ? sameDayHours : daysBetween(startDate, endDate) * 24
const limits = getBookingLimits(listing)
const belowMinimum = (totalHours || 0) < limits.minHours
const minimumMessage = `This item has a minimum hire of ${formatHirePeriod(limits.minHours)}. Please choose a longer hire period.`
const notBookableMessage = `This item has a minimum hire of ${formatHirePeriod(limits.minHours)}, which is longer than our current ${MAX_BOOKING_DAYS}-day booking limit, so it can't be booked online at the moment.`

const getDeposit = () => listing?.deposit || 0
const getPlatformFee = () => Math.round(pricing.subtotal * COMMISSION_RATE * 100) / 100
const getTotal = () => Math.round((pricing.subtotal + getDeposit()) * 100) / 100

const shareUrl = window.location.href
const shareRate = listing
? (listing.price_per_hour ? `${formatCurrencyByCountry(listing.price_per_hour, country)}/hr`
: listing.price_per_day ? `${formatCurrencyByCountry(listing.price_per_day, country)}/day`
: listing.price_per_week ? `${formatCurrencyByCountry(listing.price_per_week, country)}/wk`
: null)
: null
const shareText = listing ? `Check out ${listing.title} on HireIt${shareRate ? ` — ${shareRate}` : ''}` : ''

const handleShare = async () => {
if (navigator.share) {
try { await navigator.share({ title: listing.title, text: shareText, url: shareUrl }) } catch {}
} else {
navigator.clipboard.writeText(shareUrl)
setCopied(true)
setTimeout(() => setCopied(false), 2000)
}
}

const handleWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank')
const handleFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')
const handleSMS = () => window.open(`sms:?body=${encodeURIComponent(shareText + ' ' + shareUrl)}`)

const isDateRangeUnavailable = () => {
if (!startDate) return false
const days = bookingMode === 'sameday' ? 1 : daysBetween(startDate, endDate)
const start = new Date(startDate)
for (let i = 0; i < days; i++) {
const d = new Date(start)
d.setDate(d.getDate() + i)
const y = d.getFullYear()
const m = String(d.getMonth() + 1).padStart(2, '0')
const day = String(d.getDate()).padStart(2, '0')
if (unavailableSet.has(`${y}-${m}-${day}`)) return true
}
return false
}

const handleCalendarSelect = (dateStr) => {
setStartDate(dateStr)
if (new Date(endDate) < new Date(dateStr)) {
setEndDate(dateStr)
}
setError('')
}

const handleBook = async () => {
if (!user) {
localStorage.setItem('hireit_pending_booking', JSON.stringify({
listingId: listing.id,
bookingMode,
startDate,
endDate,
sameDayHours,
notes,
savedAt: Date.now(),
}))
navigate(`/auth?next=/listing/${listing.id}&reason=book`)
return
}
if (!cancellationCheck) { setError('Please acknowledge the cancellation policy.'); return }
if (!insuranceStatus) { setError('Please select your insurance status.'); return }
if (!termsCheck) { setError('Please agree to the Terms & Conditions.'); return }
if (bookingMode === 'multi' && new Date(endDate) < new Date(startDate)) {
setError('End date must be on or after the start date.'); return
}
if (!limits.bookableOnline) { setError(notBookableMessage); return }
if (belowMinimum) { setError(minimumMessage); return }
if (bookingMode === 'multi' && daysBetween(startDate, endDate) > 5) {
setError('Bookings are limited to 5 days at the moment. For longer hires, please book again when this one ends.'); return
}
if (isDateRangeUnavailable()) {
setError('Some of those dates are unavailable. Please pick different dates.')
return
}
setError('')
setSubmitting(true)

const total = getTotal()
const subtotal = pricing.subtotal
const platformFee = getPlatformFee()
const deposit = getDeposit()
const ownerPayout = Math.round((subtotal - platformFee) * 100) / 100

const { data: bookingData, error: bookErr } = await supabase.from('bookings').insert({
listing_id: listing.id,
hirer_id: user.id,
owner_id: listing.owner_id,
start_date: startDate,
hours: totalHours,
total_amount: total,
platform_fee: platformFee,
owner_payout: ownerPayout,
deposit_amount: deposit,
status: 'pending',
hirer_insured: insuranceStatus === 'insured',
notes,
}).select().single()

if (bookErr) { setError(bookErr.message); setSubmitting(false); return }

if (bookingData) {
const periodLabel = bookingMode === 'sameday'
? `${sameDayHours} hour${sameDayHours !== 1 ? 's' : ''} on ${startDate}`
: `${daysBetween(startDate, endDate)} day${daysBetween(startDate, endDate) > 1 ? 's' : ''} from ${startDate} to ${endDate}`

await supabase.from('messages').insert({
booking_id: bookingData.id,
sender_id: user.id,
body: `Hi! I'd like to hire your ${listing.title} for ${periodLabel}. Total: $${Number(bookingData.total_amount ?? total).toFixed(2)}`,
read: false,
})
if (notes) {
await supabase.from('messages').insert({
booking_id: bookingData.id,
sender_id: user.id,
body: notes,
read: false,
})
}
try {
const { data: ownerEmail } = await supabase.rpc('get_user_email', { user_id: listing.owner_id })
const { data: { session } } = await supabase.auth.getSession()
await fetch(`${BACKEND_URL}/notify-booking`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
// Backend looks everything up from bookingId; the other fields are kept
// only so this works with the previous backend during rollout.
bookingId: bookingData.id,
ownerEmail,
ownerName: listing.profiles?.full_name || 'Owner',
hirerName: profile?.full_name || user.email,
itemTitle: listing.title,
startDate,
hours: totalHours,
total,
})
})
} catch (e) {
console.log('Email notification failed silently:', e)
}
}

setSubmitting(false)
setStep('done')
}

if (loading) return <div><Header /><div className="spinner-wrap"><div className="spinner" /></div></div>
if (!listing) return <div><Header /><div className="container section"><p>Listing not found.</p></div></div>
if (listing.archived_at) return <div><Header /><div className="container section"><p>This listing is no longer available.</p></div></div>
if (listing.admin_disabled && !isOwner && !profile?.is_admin) return (
<div>
<Header />
<div className="container section" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
<div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
<h2 style={{ marginBottom: 8 }}>Listing not available</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>This listing has been temporarily removed from HireIt. Please browse other listings.</p>
<button className="btn btn-primary" onClick={() => navigate('/')}>Back to browse</button>
</div>
</div>
)

const priceTileStyle = {
background: 'var(--primary-pale)',
borderRadius: 10,
padding: '10px 16px',
}

const dateRangeBad = !limits.bookableOnline || belowMinimum || isDateRangeUnavailable() || (bookingMode === 'multi' && new Date(endDate) < new Date(startDate)) || (bookingMode === 'multi' && daysBetween(startDate, endDate) > 5)
const today = new Date().toISOString().split('T')[0]

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 780, padding: '1.5rem 1rem' }}>

<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
{!isOwner && (
<button className="btn btn-ghost btn-sm" onClick={toggleFavourite}>
{isFavourited ? '❤️ Saved' : '🤍 Save'}
</button>
)}
<button className="btn btn-ghost btn-sm" onClick={handleShare}>{copied ? '✅ Copied!' : '📤 Share'}</button>
<button className="btn btn-ghost btn-sm" onClick={handleSMS}>📱 SMS</button>
<button className="btn btn-ghost btn-sm" onClick={handleWhatsApp}>💬 WhatsApp</button>
<button className="btn btn-ghost btn-sm" onClick={handleFacebook}>👍 Facebook</button>
</div>
</div>

{step === 'done' ? (
<div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
<div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
<h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Booking request sent!</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
The owner has been notified and will confirm shortly. Check your bookings for updates.
</p>
<div className="alert alert-warn" style={{ textAlign: 'left', marginBottom: 24 }}>
<strong>Important:</strong> Ensure you arrange insurance for the item before collection. HireIt accepts no liability for any loss or damage.
</div>
<div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
<button className="btn btn-gradient" onClick={() => navigate('/my-bookings')}>View my bookings</button>
<button className="btn btn-ghost" onClick={() => navigate('/messages')}>💬 Message owner</button>
<button className="btn btn-ghost" onClick={() => navigate('/')}>Back to browse</button>
</div>
</div>
) : (
<div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
<div className="card">
<div style={{ position: 'relative', height: 380, background: 'var(--primary-pale)' }}>
{listing.images?.length > 0 ? (
<>
<img src={listing.images[currentImg]} alt={listing.title} style={{ width:'100%', height:'100%', objectFit:'contain' }} />
{listing.images.length > 1 && (
<>
<button onClick={() => setCurrentImg(i => (i - 1 + listing.images.length) % listing.images.length)}
style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', background:'rgba(0,0,0,0.5)', color:'#fff', border:'none', borderRadius:'50%', width:32, height:32, cursor:'pointer', fontSize:18 }}>‹</button>
<button onClick={() => setCurrentImg(i => (i + 1) % listing.images.length)}
style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'rgba(0,0,0,0.5)', color:'#fff', border:'none', borderRadius:'50%', width:32, height:32, cursor:'pointer', fontSize:18 }}>›</button>
<div style={{ position:'absolute', bottom:8, left:'50%', transform:'translateX(-50%)', display:'flex', gap:4 }}>
{listing.images.map((_, i) => (
<div key={i} onClick={() => setCurrentImg(i)} style={{ width:8, height:8, borderRadius:'50%', background: i === currentImg ? '#fff' : 'rgba(255,255,255,0.5)', cursor:'pointer' }} />
))}
</div>
</>
)}
</>
) : (
<ListingPlaceholder listing={listing} large />
)}
</div>
<div className="card-body">
<div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
<span className={`badge ${listing.available ? 'badge-green' : 'badge-amber'}`}>
{listing.available ? 'Available' : 'Currently hired'}
</span>
{cat && <span className="badge badge-gray">{cat.icon} {cat.name}</span>}
{listing.owner_insured === true && <span className="badge badge-primary">🔵 Owner insured</span>}
{listing.owner_insured === false && <span className="badge badge-amber">🟡 Not insured by owner</span>}
{listing.includes_operator && <span className="badge badge-accent">🛠️ Includes operator</span>}
{isOwner && <span className="badge badge-primary" style={{ background: 'var(--accent)', color: 'white' }}>👤 YOUR LISTING</span>}
</div>
<h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>{listing.title}</h1>
<div style={{ color: 'var(--text-muted)', marginBottom: 12 }}>
📍 {listing.full_address || `${listing.suburb}, ${listing.state} ${listing.postcode}`}
{listing.country && listing.country !== 'Australia' && <span className="badge badge-gray" style={{ marginLeft: 8 }}>{listing.country}</span>}
</div>

<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
{listing.price_per_hour && (
<div style={priceTileStyle}>
<div style={{ fontSize: 11, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Per hour</div>
<div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{formatCurrencyByCountry(listing.price_per_hour, country)}</div>
</div>
)}
{listing.price_per_day && (
<div style={priceTileStyle}>
<div style={{ fontSize: 11, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Per day</div>
<div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{formatCurrencyByCountry(listing.price_per_day, country)}</div>
</div>
)}
{listing.price_per_week && (
<div style={priceTileStyle}>
<div style={{ fontSize: 11, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Per week</div>
<div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{formatCurrencyByCountry(listing.price_per_week, country)}</div>
</div>
)}
</div>

<FormattedText text={listing.description} style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 16 }} />
<hr className="divider" />
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
{[
{ label: 'Security deposit', val: formatCurrencyByCountry(listing.deposit || 0, country) },
{ label: 'Minimum hire', val: formatHirePeriod(listing.min_hours) },
{ label: 'Owner', val: listing.profiles?.full_name || 'Anonymous', clickable: !isOwner && listing.owner_id ? `/user/${listing.owner_id}` : null },
{ label: 'Rating', val: ((listing.profiles?.review_count || 0) > 0 && listing.profiles?.rating != null && listing.profiles?.rating > 0) ? `★ ${listing.profiles.rating.toFixed(1)} (${listing.profiles.review_count} reviews)` : 'New' },
].map(item => (
<div
key={item.label}
style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '10px 14px', cursor: item.clickable ? 'pointer' : 'default' }}
onClick={() => item.clickable && navigate(item.clickable)}
>
<div style={{ fontSize: 11, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{item.label}</div>
<div style={{ fontSize: 14, fontWeight: 600, color: item.clickable ? 'var(--primary)' : 'var(--navy)' }}>
{item.val}{item.clickable && ' →'}
</div>
</div>
))}
</div>
{listing.rules && (
<>
<div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--navy)' }}>Hire rules</div>
<p style={{ fontSize: 14, color: 'var(--text-muted)', background: 'var(--amber-light)', borderRadius: 10, padding: 12 }}>{listing.rules}</p>
</>
)}
</div>
</div>

{/* OWNER VIEW — show management panel instead of booking form */}
{isOwner && (
<div className="card card-body" style={{ background: 'var(--primary-pale)', border: '2px solid var(--primary)' }}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--primary)' }}>👤 This is your listing</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.6 }}>
You're viewing your own item. Hirers will see the booking form below this. You can manage availability, view bookings, or edit your listing.
</p>
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
<button className="btn btn-gradient" onClick={() => navigate('/my-bookings')} style={{ flex: 1, minWidth: 140 }}>
📋 View bookings
</button>
<button className="btn btn-ghost" onClick={() => navigate('/profile')} style={{ flex: 1, minWidth: 140 }}>
👤 My profile
</button>
</div>
</div>
)}

<div className="card card-body">
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>📅 Availability</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
{isOwner ? 'View bookings and blocked dates for this listing.' : 'Tap any available day to set your booking start date.'}
</p>
<AvailabilityCalendar
listingId={listing.id}
mode={isOwner ? 'owner' : 'hirer'}
onDateSelect={isOwner ? null : handleCalendarSelect}
selectedDates={isOwner ? [] : [startDate]}
/>
</div>

{/* BOOKING FORM — only shown to non-owners */}
{!isOwner && listing.available && step === 'detail' && (
<div className="card card-body">
<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>Book this item</h3>

<div style={{ background: 'var(--primary-pale)', border: '1px solid var(--primary)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, fontSize: 13, color: 'var(--navy)', lineHeight: 1.6 }}>
<div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--primary)' }}>🛡️ How HireIt protects you</div>
<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
<div>✓ <strong>Refundable security deposit</strong> — covers minor damage, refunded if item returned in good condition</div>
<div>✓ <strong>Dispute resolution</strong> — our team reviews any issues as soon as possible</div>
<div>✓ <strong>Verified owner ratings</strong> — see real reviews from past hirers</div>
<div>✓ <strong>Secure Stripe payments</strong> — money held safely until the hire is complete</div>
<div>✓ <strong>In-app messaging</strong> — all communication logged for safety</div>
</div>
</div>

{!limits.bookableOnline && (
<div className="alert alert-warn" style={{ marginBottom: 12 }}>⚠️ {notBookableMessage} You can still message the owner below.</div>
)}

{limits.allowSameDay ? (
<div className="form-group" style={{ marginBottom: '1rem' }}>
<label className="form-label">How long do you need it?</label>
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
<button
onClick={() => { setBookingMode('sameday'); setEndDate(startDate) }}
className={`btn btn-sm ${bookingMode === 'sameday' ? 'btn-primary' : 'btn-ghost'}`}
style={{ flex: 1, minWidth: 140 }}
>
🕐 Same day (hours)
</button>
<button
onClick={() => setBookingMode('multi')}
className={`btn btn-sm ${bookingMode === 'multi' ? 'btn-primary' : 'btn-ghost'}`}
style={{ flex: 1, minWidth: 140 }}
>
📆 Multi-day
</button>
</div>
</div>
) : null}

{bookingMode === 'sameday' ? (
<div className="form-row" style={{ marginBottom: '1rem' }}>
<div className="form-group">
<label className="form-label">Date</label>
<input
type="date"
className="form-control"
value={startDate}
min={today}
onChange={e => { setStartDate(e.target.value); setEndDate(e.target.value) }}
/>
</div>
<div className="form-group">
<label className="form-label">How many hours?</label>
<input
type="number"
className="form-control"
value={sameDayHours}
min={limits.minHours}
max={23}
onChange={e => {
const val = e.target.value
if (val === '' || val === '0') { setSameDayHours(''); return }
setSameDayHours(Math.max(1, Math.min(23, parseInt(val) || 1)))
}}
onBlur={e => { if (!e.target.value) setSameDayHours(1) }}
/>
</div>
</div>
) : (
<div className="form-row" style={{ marginBottom: '1rem' }}>
<div className="form-group">
<label className="form-label">Start date</label>
<input
type="date"
className="form-control"
value={startDate}
min={today}
onChange={e => {
setStartDate(e.target.value)
if (new Date(endDate) < new Date(e.target.value)) setEndDate(e.target.value)
}}
/>
</div>
<div className="form-group">
<label className="form-label">End date</label>
<input
type="date"
className="form-control"
value={endDate}
min={addDays(startDate, limits.minDays - 1)}
onChange={e => setEndDate(e.target.value)}
/>
</div>
</div>
)}

{limits.bookableOnline && belowMinimum && (
<div className="alert alert-warn" style={{ marginBottom: 12 }}>⚠️ {minimumMessage}</div>
)}
{limits.minHours > 1 && limits.bookableOnline && !belowMinimum && (
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Minimum hire for this item: {formatHirePeriod(limits.minHours)}</p>
)}

{bookingMode === 'multi' && limits.bookableOnline && (
<div style={{ background: daysBetween(startDate, endDate) > 5 ? '#FEF2F2' : 'var(--primary-pale)', border: daysBetween(startDate, endDate) > 5 ? '1px solid #DC2626' : 'none', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: daysBetween(startDate, endDate) > 5 ? '#991B1B' : 'var(--navy)', textAlign: 'center' }}>
{daysBetween(startDate, endDate) > 5 ? (
<>⚠️ <strong>{daysBetween(startDate, endDate)} days</strong> — bookings are limited to 5 days. For longer hires, please book again when this one ends.</>
) : (
<>📅 <strong>{daysBetween(startDate, endDate)}</strong> {daysBetween(startDate, endDate) === 1 ? 'day' : 'days'} total
{daysBetween(startDate, endDate) >= 1 && listing.price_per_day && ' — using daily rate'}</>
)}
</div>
)}

<p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 8 }}>💡 You can also tap a day on the calendar above to set your start date</p>

{(isDateRangeUnavailable() || (bookingMode === 'multi' && new Date(endDate) < new Date(startDate))) && (
<div className="alert alert-warn" style={{ marginBottom: 12 }}>
⚠️ Some days in this range are unavailable, or the end date is before the start date. Please adjust your dates.
</div>
)}

<div className="form-group">
<label className="form-label">Notes for owner (optional)</label>
<textarea className="form-control" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any details or questions for the owner..." style={{ minHeight: 70 }} />
</div>

<div style={{ background: 'var(--primary-pale)', borderRadius: 12, padding: '1rem', marginBottom: '1rem' }}>
<div style={{ fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>Cost breakdown</div>
{[
{ label: pricing.breakdown, val: formatCurrencyByCountry(pricing.subtotal, country) },
{ label: 'Security deposit (refundable)', val: formatCurrencyByCountry(getDeposit(), country) },
].map(row => (
<div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: row.muted ? 'var(--text-faint)' : 'var(--text)', marginBottom: 6, gap: 8 }}>
<span style={{ flex: 1 }}>{row.label}</span><span style={{ whiteSpace: 'nowrap' }}>{row.val}</span>
</div>
))}
<div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16 }}>
<span style={{ color: 'var(--navy)' }}>Total</span><span style={{ color: 'var(--primary)' }}>{formatCurrencyByCountry(getTotal(), country)}</span>
</div>
</div>

<div style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
<label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.6 }}>
<input type="checkbox" checked={cancellationCheck} onChange={e => setCancellationCheck(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
<span><strong>I acknowledge the cancellation policy:</strong> 48hrs+ notice = full refund · 24-48hrs = 50% fee · Under 24hrs or no-show = 100% fee (no refund)</span>
</label>
</div>

<div style={{ marginBottom: 10 }}>
<label className="form-label" style={{ marginBottom: 8 }}>Insurance status</label>
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
<label style={{ flex: 1, minWidth: 200, display: 'flex', gap: 8, cursor: 'pointer', fontSize: 13, padding: '10px 12px', borderRadius: 10, border: `2px solid ${insuranceStatus === 'insured' ? 'var(--primary)' : 'var(--border)'}`, background: insuranceStatus === 'insured' ? 'var(--primary-light)' : 'white', alignItems: 'flex-start', transition: 'all 0.15s' }}>
<input type="checkbox" checked={insuranceStatus === 'insured'} onChange={() => setInsuranceStatus(insuranceStatus === 'insured' ? '' : 'insured')} style={{ marginTop: 2, flexShrink: 0 }} />
<span>I'm covered by my own insurance for this hire.</span>
</label>
<label style={{ flex: 1, minWidth: 200, display: 'flex', gap: 8, cursor: 'pointer', fontSize: 13, padding: '10px 12px', borderRadius: 10, border: `2px solid ${insuranceStatus === 'noinsurance' ? 'var(--primary)' : 'var(--border)'}`, background: insuranceStatus === 'noinsurance' ? 'var(--primary-light)' : 'white', alignItems: 'flex-start', transition: 'all 0.15s' }}>
<input type="checkbox" checked={insuranceStatus === 'noinsurance'} onChange={() => setInsuranceStatus(insuranceStatus === 'noinsurance' ? '' : 'noinsurance')} style={{ marginTop: 2, flexShrink: 0 }} />
<span>This item isn't covered by the owner's insurance. I'm responsible for it during the hire.</span>
</label>
</div>
</div>
<div style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
<label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.6 }}>
<input type="checkbox" checked={termsCheck} onChange={e => setTermsCheck(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
<span>I agree to HireIt's Terms & Conditions and Hire Policy including the cancellation policy. I understand payments are processed via Stripe.</span>
</label>
</div>
{error && <div className="alert alert-error">{error}</div>}
<button className="btn btn-gradient btn-lg btn-full" onClick={handleBook} disabled={submitting || dateRangeBad}>
{submitting ? 'Sending request...' : `Request to book · ${formatCurrencyByCountry(getTotal(), country)}`}
</button>
<p style={{ fontSize: 12, color: 'var(--text-faint)', textAlign: 'center', marginTop: 8 }}>
You won't be charged until the owner confirms
</p>
</div>
)}

{!isOwner && !listing.available && (
<div className="alert alert-warn">This item is currently hired out. Check back soon or contact the owner.</div>
)}

{/* MESSAGE OWNER — shown to non-owners (including guests) */}
{!isOwner && (
<div className="card card-body">
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>💬 Message the owner</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>Have a question before booking? Send the owner a message!</p>
{!showEnquiry ? (
<button className="btn btn-ghost btn-full" onClick={() => setShowEnquiry(true)}>
💬 Send a message
</button>
) : (
<>
<textarea
className="form-control"
value={enquiryMessage}
onChange={e => setEnquiryMessage(e.target.value)}
placeholder="Hi, I have a question about your item..."
style={{ minHeight: 80, marginBottom: 8 }}
/>
<div style={{ display: 'flex', gap: 8 }}>
<button className="btn btn-ghost" onClick={() => setShowEnquiry(false)}>Cancel</button>
<button className="btn btn-gradient" style={{ flex: 1 }} onClick={handleEnquiry} disabled={sendingEnquiry || !enquiryMessage.trim()}>
{sendingEnquiry ? 'Sending...' : 'Send message'}
</button>
</div>
</>
)}
</div>
)}

<div className="alert alert-warn" style={{ fontSize: 12, lineHeight: 1.5 }}>
<strong>Insurance &amp; Liability Disclaimer</strong><br />
All hirers are solely responsible for insuring items during the hire period. HireIt Pty Ltd accepts no responsibility for loss, damage, injury or any claim arising from hire transactions on this platform. See our full <a href="/terms" style={{ color: 'var(--amber)', textDecoration: 'underline' }}>Terms &amp; Conditions</a>.
</div>
</div>
)}
</div>
</div>
)
}